/* T3D · Bundles — noyau pur (validation des saisies, prix, formatage), sans DOM.
   Navigateur : window.T3DBundlesCore · Node : require() (tests/t3d-bundles-core.test.js).
   Spec : docs/superpowers/specs/2026-10-14-bundles-paliers-design.md */
(function (root, factory) {
  const core = factory();
  if (typeof module === 'object' && module.exports) module.exports = core;
  else root.T3DBundlesCore = core;
})(typeof self !== 'undefined' ? self : this, () => {
  'use strict';

  const NBSP = ' ';
  const MAX_PERCENT = 90;
  const REPARTITION_THRESHOLD = 5;

  // « 15 % » → { type: 'percent', value: 15 } · « 2,50 € » → { type: 'fixed', cents: 250 } · sinon null
  const parseDiscount = (raw) => {
    if (raw == null) return null;
    const match = String(raw).trim().match(/^([0-9]{1,3}(?:[.,][0-9]{1,2})?)\s*(%|€)$/);
    if (!match) return null;
    const amount = Number(match[1].replace(',', '.'));
    if (!(amount > 0)) return null;
    if (match[2] === '%') return amount < MAX_PERCENT ? { type: 'percent', value: amount } : null;
    return { type: 'fixed', cents: Math.round(amount * 100) };
  };

  // Lignes du métachamp custom.paliers (« quantité | remise | badge »). Une ligne invalide est ignorée.
  const parseTierLines = (lines, minUnitCents) => {
    const seen = new Set();
    const tiers = [];
    for (const line of Array.isArray(lines) ? lines : []) {
      const parts = String(line).split('|').map((part) => part.trim());
      if (parts.length < 2 || parts.length > 3 || !/^[0-9]{1,3}$/.test(parts[0])) continue;
      const qty = Number(parts[0]);
      const discount = parseDiscount(parts[1]);
      if (qty < 2 || seen.has(qty) || !discount) continue;
      if (discount.type === 'fixed' && !(discount.cents < minUnitCents)) continue;
      seen.add(qty);
      tiers.push({ qty, discount, badge: (parts[2] || '').slice(0, 30) });
    }
    return tiers.sort((a, b) => a.qty - b.qty);
  };

  // [12, 12, 34] → [{ id: 12, quantity: 2 }, { id: 34, quantity: 1 }]
  const groupUnits = (ids) => {
    const counts = new Map();
    for (const id of ids) counts.set(id, (counts.get(id) || 0) + 1);
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  };

  const basisPoints = (percent) => Math.round(percent * 100);

  // Règle mesurée dans le panier Shopify : remise % calculée par article, arrondie vers le bas.
  const lineDiscountCents = (unitCents, quantity, discount) => {
    if (!discount) return 0;
    if (discount.type === 'percent') return Math.floor((unitCents * basisPoints(discount.value)) / 10000) * quantity;
    return Math.min(discount.cents, unitCents) * quantity;
  };

  const totals = (lines, discount) => {
    const fullCents = lines.reduce((sum, line) => sum + line.unitCents * line.quantity, 0);
    const discountCents = lines.reduce((sum, line) => sum + lineDiscountCents(line.unitCents, line.quantity, discount), 0);
    return { fullCents, discountCents, finalCents: fullCents - discountCents };
  };

  // Formats Shopify usuels ({{amount}}, {{amount_with_comma_separator}}…). Les balises HTML du format sont retirées.
  const formatMoney = (cents, format) => {
    const fmt = String(format || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, '');
    const delimit = (precision, thousands, decimal) => {
      const [int, dec] = (cents / 100).toFixed(precision).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
    };
    const amounts = {
      amount: () => delimit(2, ',', '.'),
      amount_no_decimals: () => delimit(0, ',', '.'),
      amount_with_comma_separator: () => delimit(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => delimit(0, '.', ','),
      amount_with_space_separator: () => delimit(2, ' ', ','),
      amount_with_apostrophe_separator: () => delimit(2, "'", '.'),
    };
    const key = (fmt.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1];
    const value = (amounts[key] || amounts.amount_with_comma_separator)();
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/ /g, NBSP);
  };

  const discountLabel = (discount, moneyFormat) => {
    if (!discount) return '';
    if (discount.type === 'percent') return `${String(discount.value).replace('.', ',')}${NBSP}%`;
    const label = formatMoney(discount.cents, moneyFormat);
    return discount.cents % 100 === 0 ? label.replace(/[.,]00(?!\d)/, '') : label;
  };

  const buildCartItems = (mainIds, accessories = []) =>
    groupUnits([...mainIds, ...accessories.flatMap(({ id, quantity }) => Array(quantity).fill(id))]);

  const articleCount = (items) => items.reduce((sum, item) => sum + item.quantity, 0);

  const addUnit = (units, id, max) => (units.length < max ? [...units, id] : units);

  const removeUnit = (units, id) => {
    const index = units.lastIndexOf(id);
    return index < 0 ? units : [...units.slice(0, index), ...units.slice(index + 1)];
  };

  const countOf = (units, id) => units.filter((unit) => unit === id).length;

  const usesRepartition = (qty) => qty > REPARTITION_THRESHOLD;

  return {
    NBSP,
    REPARTITION_THRESHOLD,
    parseDiscount,
    parseTierLines,
    groupUnits,
    lineDiscountCents,
    totals,
    formatMoney,
    discountLabel,
    buildCartItems,
    articleCount,
    addUnit,
    removeUnit,
    countOf,
    usesRepartition,
  };
});
